/**
 * Facility-map minimap.
 *
 * A small overview of the whole map (boundary + shape silhouettes) with a
 * viewport rectangle showing the currently visible region of the main canvas.
 * Click or drag inside it to recenter the main viewport. Pure presentational +
 * a single `onNavigate(mapX, mapY)` callback.
 */

import { useRef } from "react";
import { pointsToSvgString, type Point } from "./shapeUtils";
import type { YardFeature } from "@/types/yard";

interface MiniSection {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
}

interface MapMinimapProps {
  yardWidth: number;
  yardHeight: number;
  boundaryPoints: Point[] | null;
  sections: MiniSection[];
  features: YardFeature[];
  /** Main-canvas scale (px per map unit) and its viewport rect (px). */
  scale: number;
  view: { left: number; top: number; cw: number; ch: number };
  /** Recenter the main viewport on this map coordinate. */
  onNavigate: (mapX: number, mapY: number) => void;
}

const MINI_W = 168;
const MINI_H = 120;
const PAD = 6;

function MapMinimap({
  yardWidth,
  yardHeight,
  boundaryPoints,
  sections,
  features,
  scale,
  view,
  onNavigate,
}: MapMinimapProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  // Scale the full map into the minimap box (contain).
  const k = Math.min(
    (MINI_W - PAD * 2) / yardWidth,
    (MINI_H - PAD * 2) / yardHeight,
  );
  const offX = PAD + (MINI_W - PAD * 2 - yardWidth * k) / 2;
  const offY = PAD + (MINI_H - PAD * 2 - yardHeight * k) / 2;
  const tx = (mx: number) => offX + mx * k;
  const ty = (my: number) => offY + my * k;

  // Viewport rect in map units: the main canvas draws map coord at
  // pixel = coord*scale + 20, so map = (pixel - 20)/scale.
  const vx = (view.left - 20) / scale;
  const vy = (view.top - 20) / scale;
  const vw = view.cw / scale;
  const vh = view.ch / scale;

  const navFromEvent = (e: React.PointerEvent) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    onNavigate((px - offX) / k, (py - offY) / k);
  };

  const dragging = useRef(false);

  return (
    <div className="absolute right-2 top-2 z-20 overflow-hidden rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-raised)]/95 shadow-[var(--shadow-md)] backdrop-blur">
      <svg
        ref={svgRef}
        width={MINI_W}
        height={MINI_H}
        className="block cursor-pointer"
        onPointerDown={(e) => {
          dragging.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          navFromEvent(e);
        }}
        onPointerMove={(e) => {
          if (dragging.current) navFromEvent(e);
        }}
        onPointerUp={(e) => {
          dragging.current = false;
          e.currentTarget.releasePointerCapture(e.pointerId);
        }}
      >
        {/* Map extent */}
        <rect
          x={tx(0)}
          y={ty(0)}
          width={yardWidth * k}
          height={yardHeight * k}
          fill="var(--surface-sunken, #eef)"
          stroke="var(--border-subtle, #ddd)"
          strokeWidth={1}
        />

        {/* Boundary */}
        {boundaryPoints && boundaryPoints.length >= 3 && (
          <polygon
            points={pointsToSvgString(
              boundaryPoints.map((p) => ({ x: tx(p.x), y: ty(p.y) })),
            )}
            fill="none"
            stroke="var(--color-border, #94a3b8)"
            strokeWidth={1}
          />
        )}

        {/* Feature silhouettes */}
        {features.map((f) =>
          f.points && f.points.length >= 2 ? (
            <polygon
              key={f.id}
              points={pointsToSvgString(
                f.points.map((p) => ({ x: tx(p.x), y: ty(p.y) })),
              )}
              fill="#94a3b8"
              fillOpacity={f.geometry_type === "polyline" ? 0 : 0.4}
              stroke="#64748b"
              strokeWidth={0.75}
            />
          ) : null,
        )}

        {/* Section silhouettes */}
        {sections.map((s) => (
          <rect
            key={s.id}
            x={tx(s.x)}
            y={ty(s.y)}
            width={Math.max(1, s.width * k)}
            height={Math.max(1, s.height * k)}
            fill={s.color}
            fillOpacity={0.55}
          />
        ))}

        {/* Viewport rectangle */}
        <rect
          x={tx(vx)}
          y={ty(vy)}
          width={Math.max(2, vw * k)}
          height={Math.max(2, vh * k)}
          fill="var(--color-interactive-primary, #7c3aed)"
          fillOpacity={0.12}
          stroke="var(--color-interactive-primary, #7c3aed)"
          strokeWidth={1.25}
        />
      </svg>
    </div>
  );
}

export default MapMinimap;
